import { getAssetPack } from "./AssetPack";
import AssetPack from "./AssetPack";
import { fail } from "../utils/Assert";
import { exists, get, getElse } from "../utils/Safe";

/**
 * The AssetLoader is a helper object that wraps bulk this.game.load calls 
 * into a single function call. This object will help reduce bloat in a 
 * Phaser.State::preload by moving all asset key/url 's to an external manifest.
 * 
 * Dependencies: manifest.json 
 * 
 * Manifest:
 * A manifest is a collection of asset key/url information separated into asset packs.
 * 
 * Example:
 * {
 *     "main": {
 *         "images": [
 *             { "key": "loadingBackground", "url": "assets/main/images/loadingBackground.png" }
 *         ],
 *         "json": [
 *             { "key": "mainConfig", "url": "assets/main/config.json" }
 *         ]
 *     },
 *     "title": {
 *         "images": [
 *             { "key": "titleBackground", "url": "assets/title/images/titleBackground.png" }
 *         ],
 *         "json": [
 *             { "key": "titleConfig", "url": "assets/title/config.json" }
 *         ]
 *     }
 * }
 * This is a simple manifest consisting of two asset packs: main and title.
 */
export default class AssetLoader
{
    constructor()
    {
        /**
         * A map of asset packs defined by a manifest.
         * @type {Object.<string, AssetPack>}
         */
        this.assetPacks = { };
    }

    /**
     * Cleans up asset loader properties.
     */
    destroy()
    {
        let keys = Object.keys(this.assetPacks);
        while (keys.length > 0)
        {
            this.assetPacks[keys[0]].destroy();
            delete this.assetPacks[keys[0]];
            keys.shift();
        }
    }

    /**
     * Sets up Phaser.Loader objects for Phaser.State::preload.
     * Use this inside of the Phaser.State preload function only.
     * @param {Phaser.Game} game 
     * @param {string[]} assetPackKeys 
     */
    preloadAssetPacks(game, assetPackKeys)
    {
        // Begin setting up loaders for each asset pack provided.
        for (let i = 0; i < assetPackKeys.length; i++)
        {
            let key = assetPackKeys[i];
            // Make sure the asset pack exists within the asset pack map.
            if (exists(this.assetPacks[key]))
            {
                // Begin adding loaders for each asset group within the asset pack.
                this.addImageLoaders(game, this.assetPacks[key].images);
                this.addJsonLoaders(game, this.assetPacks[key].json);
                this.addFlumpLoaders(game, this.assetPacks[key].flump);
                this.addAudioLoaders(game, this.assetPacks[key].audio);
            }
        }
    }
    
    /**
     * Loads a set of asset packs. 
     * Use this outside of the Phaser.State preload function only.
     * @param {Phaser.Game} game 
     * @param {string[]} assetPackKeys 
     */
    loadAssetPacks(game, assetPackKeys)
    {
        // Calling preloadAssetPacks here will setup the Phaser.Loader.
        this.preloadAssetPacks(game, assetPackKeys);
        // Starting loading.
        game.load.start();
    }

    /**
     * Unloads all assets belonging to the provided asset packs.
     * @param {string[]} assetPackKeys 
     */
    unloadAssetPacks(game, assetPackKeys)
    {
        // Begin unloading each asset pack provided.
        for (let i = 0; i < assetPackKeys.length; i++)
        {
            let key = assetPackKeys[i];
            // Make sure the asset pack exists within the asset pack map.
            if (exists(this.assetPacks[key]))
            {
                // Begin unloading each asset group within the asset pack.
                this.unloadImages(game, this.assetPacks[key].images);
                this.unloadJson(game, this.assetPacks[key].json);
                this.unloadFlump(game, this.assetPacks[key].flump);
                this.unloadAudio(game, this.assetPacks[key].audio);
            }
        }
    }

    /** 
     * Adds a listener to Phaser.Loader::onLoadStart.
     * @param {Phaser.Game} game 
     * @param {function} callback 
     * @param {*} ctx 
     */
    addStartCallback(game, callback, ctx)
    {
        game.load.onLoadStart.addOnce(callback, ctx);
    }

    /** 
     * Adds a listener to Phaser.Loader::onFileComplete.
     * @param {Phaser.Game} game 
     * @param {function} callback 
     * @param {*} ctx 
     */
    addProgressCallback(game, callback, ctx)
    {
        game.load.onFileComplete.add(callback, ctx);
    }
    
    /** 
     * Adds a listener to Phaser.Loader::onLoadComplete.
     * @param {Phaser.Game} game 
     * @param {function} callback 
     * @param {*} ctx 
     */
    addCompleteCallback(game, callback, ctx)
    {
        game.load.onLoadComplete.addOnce(callback, ctx);
    }
    
    /** 
     * Removes a listener from Phaser.Loader::onLoadStart.
     * @param {Phaser.Game} game 
     * @param {function} callback 
     * @param {*} ctx 
     */
    removeStartCallback(game, callback, ctx)
    {
        game.load.onLoadStart.remove(callback, ctx);
    }
    
    /** 
     * Removes a listener from Phaser.Loader::onFileComplete.
     * @param {Phaser.Game} game 
     * @param {function} callback 
     * @param {*} ctx 
     */
    removeProgressCallback(game, callback, ctx)
    {
        game.load.onFileComplete.remove(callback, ctx);
    }
    
    /** 
     * Removes a listener from Phaser.Loader::onLoadComplete.
     * @param {Phaser.Game} game 
     * @param {function} callback 
     * @param {*} ctx 
     */
    removeCompleteCallback(game, callback, ctx)
    {
        game.load.onLoadComplete.remove(callback, ctx);
    }

    /**
     * Parses the manifest into AssetPacks. See docs for manifest format.
     * @param {*} manifest 
     */
    parseManifest(manifest)
    {
        // Makes sure the manifest exists before trying to parse it.
        if (exists(manifest))
        {
            for (let key in manifest)
            {
                // Create each asset pack in the manifest and 
                // add them to the asset loader's asset pack map.
                this.assetPacks[key] = getAssetPack(manifest[key]);
            }
        }
    }

    // Private Methods

    /**
     * Loads assets as images. This is meant to be used internally.
     * @param {Phaser.Game} game 
     * @param {*[]} assets 
     * @internal
     */
    addImageLoaders(game, assets)
    {
        // For each image in the asset gorup, add a Phaser.Loader.
        assets.forEach(
            function(element)
            {
                if (exists(element.key) && exists(element.url))
                {
                    game.load.image(element.key, element.url);
                }
                else
                {
                    fail(`[AssetLoader] Provided image key or url is missing from: ${element}`);
                }
            }
        );
    }

    /**
     * Loads assets as json. This is meant to be used internally.
     * @param {Phaser.Game} game 
     * @param {*[]} assets 
     * @internal
     */
    addJsonLoaders(game, assets)
    {
        assets.forEach(
            function(element)
            {
                if (exists(element.key) && exists(element.url))
                {
                    game.load.json(element.key, element.url);
                }
                else
                {
                    fail(`[AssetLoader] Provided image key or url is missing from: ${element}`);
                }
            }
        );
    }

    /**
     * Loads assets for Flump animations. Loads pngs as images and library as json.
     * @param {Phaser.Game} game 
     * @param {*[]} assets 
     */
    addFlumpLoaders(game, assets)
    {
        assets.forEach(
            function(element)
            {
                if (exists(element.key) && exists(element.pngs) && exists(element.library))
                {
                    let pngCount = getElse([element.pngs, "length"], 0);
                    for (let i = 0; i < pngCount; i++)
                    {
                        // Our Flump animation player expects the pngs to be keyed by their url.
                        game.load.image(element.pngs[i], element.pngs[i]);
                    }
                    game.load.json(element.key, element.library);
                }
                else
                {
                    fail(`[AssetLoader] Provided flump key, pngs, or library is missing from: ${element}`);
                }      
            }
        );
    }
    
    /**
     * Loads assets as audio. This is meant to be used internally.
     * @param {Phaser.Game} game 
     * @param {*[]} assets 
     * @internal
     */
    addAudioLoaders(game, assets)
    {
        assets.forEach(
            function(element)
            {
                if (exists(element.key) && exists(element.url))
                {
                    game.load.audio(element.key, element.url);
                    // TODO - Might want to add a flag to decode mp3s and a way to delay
                    // callbacks until decoding is complete.
                }
                else
                {
                    fail(`[AssetLoader] Provided audio key ` + element.key + ` or url ` + element.url +` is missing from: ${element}`);
                }
            }
        );
    }

    /**
     * Unloads assets as images. This is meant to be used internally.
     * @param {Phaser.Game} game 
     * @param {*[]} assets 
     * @internal
     */
    unloadImages(game, assets)
    {
        assets.forEach(
            function(element)
            {
                if (exists(element.key))
                {
                    game.cache.removeImage(element.key, true);
                }
                else
                {
                    fail(`[AssetLoader] Cannot unload image ${element}`);
                }
            }
        );
    }

    /**
     * Unloads assets as json. This is meant to be used internally.
     * @param {Phaser.Game} game 
     * @param {*[]} assets 
     * @internal
     */
    unloadJson(game, assets)
    {
        assets.forEach(
            function(element)
            {
                if (exists(element.key))
                {
                    game.cache.removeJSON(element.key, true);
                }
                else
                {
                    fail(`[AssetLoader] Cannot unload json ${element}`);
                }
            }
        );
    }

    /**
     * Unloads assets for Flump animations. Unload pngs as images and library as json.
     * @param {Phaser.Game} game 
     * @param {*[]} assets 
     */
    unloadFlump(game, assets)
    {
        assets.forEach(
            function(element)
            {
                if (exists(element.key) && exists(element.pngs) && exists(element.library))
                {
                    let pngCount = getElse([element.pngs, "length"], 0);
                    for (let i = 0; i < pngCount; i++)
                    {
                        // Our Flump animation player expects the pngs to be keyed by their url.
                        game.cache.removeImage(element.pngs[i], true);
                    }
                    game.cache.removeJSON(element.key);
                }
                else
                {
                    fail(`[AssetLoader] Cannot unload flump ${element}`);
                }
            }
        );
    }

    /**
     * Unloads assets as audio. This is meant to be used internally.
     * @param {Phaser.Game} game 
     * @param {*[]} assets 
     * @internal
     */
    unloadAudio(game, assets)
    {
        assets.forEach(
            function(element)
            {
                if (exists(element.key))
                {
                    game.cache.removeSound(element.key);
                }
                else
                {
                    fail(`[AssetLoader] Cannot unload audio ${element}`);
                }
            }
        );
    }
}